import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAsset } from '../lib/api/assets';
import {
  BannerRef,
  COPY_KEYS,
  CopyKey,
  CopyOption,
  ShopBannerInput,
  ShopBannerStyle,
  Translations,
  errorDetail,
  fetchShopTranslations,
  suggestShopBannerCopy,
  translateShopBannerCopy,
  useCollectionSearch,
  usePublishShopBanner,
  useSaveShopBanner,
  useShopBanner,
  useShopBannerMeta,
  useUnpublishShopBanner,
} from '../lib/api/shopBanners';
import { assetDisplayTitle } from '../lib/assetTitle';
import { Button } from '../components/ui/Button';
import { Field } from '../components/ui/Field';
import { Icon } from '../components/ui/Icon';
import { useToast } from '../components/ui/Toast';
import ShopBannerPreview from '../components/shopBanners/ShopBannerPreview';
import ShopBannerStatus, { RecurringStatusPill, windowSentence } from '../components/shopBanners/ShopBannerStatus';
import {
  END_OFFSETS,
  LOCAL_TIME_NOTE,
  WEEKDAYS,
  fromHHMM,
  scheduleSentence,
  splitEnd,
  toHHMM,
  upcomingWindows,
  windowMinutes,
} from '../components/shopBanners/schedule';
import styles from './ShopBannerEditor.module.css';

const STYLES: { key: ShopBannerStyle; label: string; bg: string; fg: string }[] = [
  { key: 'yellow', label: 'Yellow', bg: '#ffbd09', fg: '#2b4138' },
  { key: 'green', label: 'Dark green', bg: '#2b4138', fg: '#ffbd09' },
  { key: 'black', label: 'Black', bg: '#111111', fg: '#ffbd09' },
];

const COPY_LABEL: Record<CopyKey, string> = {
  eyebrow: 'Small heading',
  headline: 'Headline',
  subline: 'Subline',
  small_print: 'Small print',
};

const LOCALE_NAME: Record<string, string> = {
  nl: 'Dutch', fr: 'French', de: 'German', es: 'Spanish', it: 'Italian',
  da: 'Danish', pl: 'Polish', 'pt-PT': 'Portuguese',
};

const SHOP_URL = 'https://lamptwist.com';

interface Form {
  collection_id: string;
  collection_title: string;
  collection_handle: string;
  start_date: string;
  end_date: string;
  countries: string;
  priority: string;
  style: ShopBannerStyle;
  timer_hours: string;
  eyebrow: string;
  headline: string;
  subline: string;
  small_print: string;
  translations: Translations;
  // Recurring banners only.
  repeat_days: number[];
  start_time: string;
  end_time: string;
  end_offset: number;
  paused_from: string;
  paused_until: string;
  enabled: boolean;
}

const scheduleInput = (f: Form): ShopBannerInput => ({
  repeat_days: f.repeat_days,
  repeat_start_minute: fromHHMM(f.start_time),
  repeat_minutes: windowMinutes(f.start_time, f.end_time, f.end_offset),
  paused_from: f.paused_from || null,
  paused_until: f.paused_until || null,
  enabled: f.enabled,
});

const toInput = (f: Form, recurring: boolean): ShopBannerInput => ({
  collection_id: f.collection_id,
  collection_title: f.collection_title,
  collection_handle: f.collection_handle,
  ...(recurring ? scheduleInput(f) : { start_date: f.start_date || null, end_date: f.end_date || null }),
  countries: f.countries.split(/[\s,;]+/).map((c) => c.trim()).filter(Boolean),
  priority: Number(f.priority) || 0,
  style: f.style,
  timer_hours: f.timer_hours.trim() === '' ? null : Number(f.timer_hours),
  eyebrow: f.eyebrow,
  headline: f.headline,
  subline: f.subline,
  small_print: f.small_print,
  translations: f.translations,
});

export const ShopBannerEditor = () => {
  const { assetId, recurringId } = useParams<{ assetId: string; recurringId: string }>();
  // Recurring banners (Nightly / Weekend sale) share this editor; they have a
  // weekly schedule instead of dates and no placement.
  const recurring = !!recurringId;
  const ref: BannerRef = recurring
    ? { kind: 'recurring', id: Number(recurringId) }
    : { kind: 'placement', id: Number(assetId) };
  const placementId = recurring ? undefined : ref.id;
  const toast = useToast();
  const { data: asset } = useAsset(placementId);
  const { data: banner, isLoading, error: loadError } = useShopBanner(ref);
  const { data: meta } = useShopBannerMeta();
  const save = useSaveShopBanner(ref);
  const publish = usePublishShopBanner(ref);
  const unpublish = useUnpublishShopBanner(ref);

  const [form, setForm] = useState<Form | null>(null);
  const [dirty, setDirty] = useState(false);
  const [locale, setLocale] = useState<string>('');
  const [previewLocale, setPreviewLocale] = useState('en');
  const [showCountdown, setShowCountdown] = useState(true);
  const [narrow, setNarrow] = useState(false);
  const [collectionQuery, setCollectionQuery] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [loadingShop, setLoadingShop] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [brief, setBrief] = useState('');
  const [suggesting, setSuggesting] = useState(false);
  const [options, setOptions] = useState<CopyOption[]>([]);
  const [translating, setTranslating] = useState(false);

  useEffect(() => {
    if (!banner || (form && dirty)) return;
    const end = splitEnd(banner.repeat_start_minute ?? 0, banner.repeat_minutes ?? 0);
    setForm({
      collection_id: banner.collection_id,
      collection_title: banner.collection_title,
      collection_handle: banner.collection_handle,
      start_date: banner.start_date ?? '',
      end_date: banner.end_date ?? '',
      countries: banner.countries.join(', '),
      priority: String(banner.priority ?? 0),
      style: banner.style,
      timer_hours: banner.timer_hours == null ? '' : String(banner.timer_hours),
      eyebrow: banner.eyebrow,
      headline: banner.headline,
      subline: banner.subline,
      small_print: banner.small_print,
      translations: banner.translations ?? {},
      repeat_days: banner.repeat_days ?? [],
      start_time: toHHMM(banner.repeat_start_minute ?? 0),
      end_time: end.endTime,
      end_offset: end.offset,
      paused_from: banner.paused_from ?? '',
      paused_until: banner.paused_until ?? '',
      enabled: banner.enabled ?? true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [banner]);

  const order = Object.keys(LOCALE_NAME);
  const rank = (l: string) => (order.indexOf(l) === -1 ? 99 : order.indexOf(l));
  const locales = [...(meta?.locales?.length ? meta.locales : order)].sort((a, b) => rank(a) - rank(b));
  useEffect(() => {
    if (!locale && locales.length) setLocale(locales[0]);
  }, [locale, locales]);

  const search = useCollectionSearch(collectionQuery.trim(), pickerOpen && collectionQuery.trim().length >= 2);

  const set = <K extends keyof Form>(k: K, v: Form[K]) => {
    setForm((f) => (f ? { ...f, [k]: v } : f));
    setDirty(true);
  };
  const setTranslation = (loc: string, key: CopyKey, v: string) => {
    setForm((f) => {
      if (!f) return f;
      return { ...f, translations: { ...f.translations, [loc]: { ...(f.translations[loc] ?? {}), [key]: v } } };
    });
    setDirty(true);
  };

  const previewCopy = useMemo(() => {
    if (!form) return null;
    const tr = previewLocale === 'en' ? {} : form.translations[previewLocale] ?? {};
    const pick = (k: CopyKey) => (tr[k] ?? '').trim() || form[k];
    return { eyebrow: pick('eyebrow'), headline: pick('headline'), subline: pick('subline'), small_print: pick('small_print') };
  }, [form, previewLocale]);

  if (isLoading || !form) {
    return (
      <div className={styles.empty}>
        {loadError ? errorDetail(loadError) ?? 'Could not load this shop banner.' : 'Loading…'}
      </div>
    );
  }

  const busy = save.isPending || publish.isPending || unpublish.isPending;
  const published = !!banner?.shopify_id && banner.shopify_status === 'ACTIVE';
  const timerOff = form.timer_hours.trim() === '0';
  const startMinute = fromHHMM(form.start_time);
  const repeatMinutes = windowMinutes(form.start_time, form.end_time, form.end_offset);

  const doSave = async (quiet = false) => {
    try {
      const res = await save.mutateAsync(toInput(form, recurring));
      setDirty(false);
      if (!quiet) {
        toast.push(recurring && !res.enabled && banner?.shopify_id
          ? 'Saved — switched off, so it is not on the shop'
          : 'Shop banner saved');
      }
      return true;
    } catch (err) {
      toast.push(`Could not save — ${errorDetail(err) ?? 'please try again'}`);
      return false;
    }
  };

  const doPublish = async () => {
    if (!(await doSave(true))) return;
    try {
      const res = await publish.mutateAsync();
      toast.push(res.shopify_status === 'ACTIVE' ? 'Sent to the shop' : 'Sent to the shop as a draft (test environment)');
    } catch (err) {
      toast.push(`Could not publish — ${errorDetail(err) ?? 'please try again'}`);
    }
  };

  const doUnpublish = async () => {
    try {
      await unpublish.mutateAsync();
      toast.push('Taken off the shop');
    } catch (err) {
      toast.push(`Could not unpublish — ${errorDetail(err) ?? 'please try again'}`);
    }
  };

  const loadFromShop = async () => {
    setLoadingShop(true);
    try {
      const shop = await fetchShopTranslations(ref);
      let filled = 0;
      setForm((f) => {
        if (!f) return f;
        const next: Translations = { ...f.translations };
        Object.entries(shop).forEach(([loc, vals]) => {
          const cur = { ...(next[loc] ?? {}) };
          COPY_KEYS.forEach((k) => {
            if (!(cur[k] ?? '').trim() && vals[k]) { cur[k] = vals[k]; filled += 1; }
          });
          next[loc] = cur;
        });
        return { ...f, translations: next };
      });
      if (filled) setDirty(true);
      toast.push(filled ? `Filled ${filled} empty text(s) from the shop` : 'No extra translations on the shop');
    } catch (err) {
      toast.push(`Could not read the shop — ${errorDetail(err) ?? 'please try again'}`);
    } finally {
      setLoadingShop(false);
    }
  };

  const aiReady = !!meta?.ai_configured;
  const aiOffNote = 'The Claude connection is not set up yet (ANTHROPIC_API_KEY in Coolify).';

  const getSuggestions = async () => {
    setSuggesting(true);
    try {
      const res = await suggestShopBannerCopy(ref, {
        brief,
        collection_title: form.collection_title,
        ...(recurring
          ? {
              repeat_days: form.repeat_days,
              repeat_start_minute: Number.isNaN(startMinute) ? 0 : startMinute,
              repeat_minutes: repeatMinutes > 0 ? repeatMinutes : 0,
              timer_hours: form.timer_hours.trim() === '' ? null : Number(form.timer_hours),
            }
          : { start_date: form.start_date || null, end_date: form.end_date || null }),
        eyebrow: form.eyebrow,
        headline: form.headline,
        subline: form.subline,
        small_print: form.small_print,
      });
      setOptions(res);
    } catch (err) {
      toast.push(`No suggestions — ${errorDetail(err) ?? 'please try again'}`);
    } finally {
      setSuggesting(false);
    }
  };

  const applyOption = (o: CopyOption) => {
    setForm((f) => (f ? { ...f, eyebrow: o.eyebrow, headline: o.headline, subline: o.subline, small_print: o.small_print } : f));
    setDirty(true);
    setOptions([]);
    setSuggestOpen(false);
    toast.push('Text filled in — translations still follow the old English until you translate again');
  };

  // overwrite = false: only fill empty translation fields; true: redo every language.
  const translate = async (overwrite: boolean) => {
    if (!form.headline.trim()) {
      toast.push('Write the English headline first');
      return;
    }
    const targets = overwrite ? locales : locales.filter((l) => translatedCount(l) < COPY_KEYS.length);
    if (!targets.length) {
      toast.push('Every language is already filled in');
      return;
    }
    setTranslating(true);
    try {
      const res = await translateShopBannerCopy(
        ref,
        { eyebrow: form.eyebrow, headline: form.headline, subline: form.subline, small_print: form.small_print },
        targets,
      );
      let filled = 0;
      setForm((f) => {
        if (!f) return f;
        const next: Translations = { ...f.translations };
        Object.entries(res).forEach(([loc, vals]) => {
          const cur = { ...(next[loc] ?? {}) };
          COPY_KEYS.forEach((k) => {
            if (overwrite || !(cur[k] ?? '').trim()) {
              if ((cur[k] ?? '') !== vals[k]) filled += 1;
              cur[k] = vals[k];
            }
          });
          next[loc] = cur;
        });
        return { ...f, translations: next };
      });
      setDirty(true);
      toast.push(filled ? `Translated ${targets.length} language(s) — check them before publishing` : 'Nothing to change');
    } catch (err) {
      toast.push(`Could not translate — ${errorDetail(err) ?? 'please try again'}`);
    } finally {
      setTranslating(false);
    }
  };

  const title = recurring
    ? banner?.name ?? 'Recurring banner'
    : asset ? assetDisplayTitle(asset, { withCampaign: true }) : `Placement ${placementId}`;
  const toggleDay = (d: number) =>
    set('repeat_days', form.repeat_days.includes(d) ? form.repeat_days.filter((x) => x !== d) : [...form.repeat_days, d].sort((a, b) => a - b));
  const translatedCount = (loc: string) =>
    COPY_KEYS.filter((k) => (form.translations[loc]?.[k] ?? '').trim()).length;

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-eyebrow">
            <Link to="/shop-banners" className={styles.crumb}>Shop banners &amp; timers</Link>
            {recurring && <> · Recurring</>}
            {!recurring && asset?.campaign && (
              <> · <Link to={`/campaigns/${asset.campaign}`} className={styles.crumb}>{asset.campaign_name}</Link></>
            )}
          </div>
          <h1 className="page-title">{title}</h1>
          <div style={{ marginTop: 8 }}>
            {recurring ? (
              <RecurringStatusPill
                status={banner?.recurring_status ?? null}
                inSync={dirty && banner?.shopify_id ? false : banner?.in_sync ?? null}
              />
            ) : (
              <ShopBannerStatus
                state={banner?.live_state ?? 'Draft'}
                inSync={dirty && banner?.shopify_id ? false : banner?.in_sync ?? null}
                notSetUp={!banner?.exists}
              />
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          {published && (
            <Button variant="ghost" onClick={doUnpublish} disabled={busy}>Take off the shop</Button>
          )}
          <Button onClick={() => doSave()} disabled={busy || !dirty}>Save</Button>
          <Button
            variant="primary"
            onClick={doPublish}
            disabled={busy || meta?.configured === false || (recurring && !form.enabled)}
            title={recurring && !form.enabled ? 'Switch the banner on first (Schedule card)' : undefined}
          >
            {banner?.shopify_id ? 'Update shop' : 'Publish to shop'}
          </Button>
        </div>
      </div>

      {meta && !meta.configured && (
        <div className={styles.notice}>
          The shop isn't connected to mosy yet, so you can prepare the banner but not publish it.
        </div>
      )}
      {meta?.configured && !meta.publish_active && (
        <div className={styles.notice}>
          Test version of mosy: banners go to the shop as a <strong>draft</strong>, so shoppers never see them.
        </div>
      )}
      {banner?.push_error && (
        <div className={`${styles.notice} ${styles.noticeWarn}`}>Last publish failed: {banner.push_error}</div>
      )}

      <div className={styles.layout}>
        <div className={styles.formCol}>
          {/* allow-overflow: the collection search results drop down over the next card */}
          <section className="card allow-overflow" style={{ padding: 18, position: 'relative', zIndex: 2 }}>
            <h2 className={styles.h2}>Products</h2>
            <Field
              label="Collection"
              required={!recurring}
              hint={recurring
                ? 'Empty = all products. Otherwise the banner shows on every product in this collection (edit the collection in Shopify to change them).'
                : 'The banner shows on every product in this collection. Change the products by editing the collection in Shopify.'}
            >
              {form.collection_id && !pickerOpen ? (
                <div className={styles.picked}>
                  <div>
                    <strong>{form.collection_title}</strong>
                    {form.collection_handle && (
                      <a href={`${SHOP_URL}/collections/${form.collection_handle}`} target="_blank" rel="noreferrer" className={styles.shopLink}>
                        /collections/{form.collection_handle} ↗
                      </a>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {recurring && (
                      <Button
                        variant="ghost"
                        onClick={() => {
                          setForm((f) => f && { ...f, collection_id: '', collection_title: '', collection_handle: '' });
                          setDirty(true);
                        }}
                      >
                        All products
                      </Button>
                    )}
                    <Button variant="ghost" onClick={() => { setPickerOpen(true); setCollectionQuery(''); }}>Change</Button>
                  </div>
                </div>
              ) : (
                <div className={styles.picker}>
                  <input
                    className="input"
                    autoFocus={pickerOpen}
                    value={collectionQuery}
                    onFocus={() => setPickerOpen(true)}
                    onChange={(e) => { setCollectionQuery(e.target.value); setPickerOpen(true); }}
                    placeholder={recurring ? 'All products — or search a collection, e.g. Nightly Sale' : 'Search a collection by name, e.g. Serax'}
                  />
                  {pickerOpen && collectionQuery.trim().length >= 2 && (
                    <div className={styles.results}>
                      {search.isLoading && <div className={styles.resultNote}>Searching…</div>}
                      {search.error && <div className={styles.resultNote}>{errorDetail(search.error) ?? 'Search failed'}</div>}
                      {search.data?.length === 0 && <div className={styles.resultNote}>No collection found</div>}
                      {search.data?.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          className={styles.result}
                          onClick={() => {
                            setForm((f) => f && { ...f, collection_id: c.id, collection_title: c.title, collection_handle: c.handle });
                            setDirty(true);
                            setPickerOpen(false);
                            if (c.published === false) {
                              toast.push('Heads-up: this collection is not on the Online Store, so the banner won\'t show. Publish it in Shopify (it can stay out of the menu).');
                            }
                          }}
                        >
                          <span>{c.title}</span>
                          <span className={styles.resultMeta}>
                            {c.products_count ?? '?'} products
                            {c.published === false && <span className={styles.warnTag}>not on Online Store</span>}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                  {form.collection_id && (
                    <button type="button" className={styles.linkBtn} onClick={() => setPickerOpen(false)}>
                      Keep {form.collection_title}
                    </button>
                  )}
                </div>
              )}
            </Field>
          </section>

          <section className="card" style={{ padding: 18 }}>
            {recurring ? (
              <>
                <div className={styles.h2Row}>
                  <h2 className={styles.h2}>Schedule</h2>
                  <label className={styles.toggle} title="Off = not on the shop (saving takes it off)">
                    <input type="checkbox" checked={form.enabled} onChange={(e) => set('enabled', e.target.checked)} />
                    {form.enabled ? 'On' : 'Off'}
                  </label>
                </div>
                <p className={styles.help}>{LOCAL_TIME_NOTE}</p>
                <Field label="Starts on" required hint="The days the window starts on, every week.">
                  <div className={styles.days}>
                    {WEEKDAYS.map((label, i) => (
                      <label key={label} className={`${styles.day} ${form.repeat_days.includes(i + 1) ? styles.dayOn : ''}`}>
                        <input type="checkbox" checked={form.repeat_days.includes(i + 1)} onChange={() => toggleDay(i + 1)} />
                        {label}
                      </label>
                    ))}
                  </div>
                </Field>
                <div className={styles.grid3}>
                  <Field label="Starts at" required>
                    <input className="input" type="time" value={form.start_time} onChange={(e) => set('start_time', e.target.value)} />
                  </Field>
                  <Field label="Ends at" required>
                    <input className="input" type="time" value={form.end_time} onChange={(e) => set('end_time', e.target.value)} />
                  </Field>
                  <Field label="Ends on">
                    <select className="input" value={form.end_offset} onChange={(e) => set('end_offset', Number(e.target.value))}>
                      {END_OFFSETS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </Field>
                </div>
                <div className={styles.sentence}>
                  {Number.isNaN(startMinute) || Number.isNaN(repeatMinutes)
                    ? 'Pick a start and an end time.'
                    : scheduleSentence(form.repeat_days, startMinute, repeatMinutes)}
                  {upcomingWindows(form.repeat_days, startMinute, repeatMinutes, form.paused_from, form.paused_until)
                    .map((w, i) => (
                      <div key={i} className={styles.sentenceWindow}>
                        {w.live ? 'Live now: ' : i === 0 ? 'Next: ' : 'Then: '}{w.text}{i === 0 ? ' · Brussels time' : ''}
                      </div>
                    ))}
                </div>
                <div className={styles.grid2} style={{ marginTop: 12 }}>
                  <Field label="Pause from (optional)" hint="First day without the banner (Brussels).">
                    <input className="input" type="date" value={form.paused_from} onChange={(e) => set('paused_from', e.target.value)} />
                  </Field>
                  <Field label="Pause until" hint="Last day without the banner (included).">
                    <input className="input" type="date" value={form.paused_until} onChange={(e) => set('paused_until', e.target.value)} />
                  </Field>
                </div>
              </>
            ) : (
              <>
                <h2 className={styles.h2}>When</h2>
                <div className={styles.grid2}>
                  <Field label="Start date" required hint="Live from 00:00 Brussels time.">
                    <input className="input" type="date" value={form.start_date} onChange={(e) => set('start_date', e.target.value)} />
                  </Field>
                  <Field label="End date" required hint="Gone at 00:00 Brussels time on this day.">
                    <input className="input" type="date" value={form.end_date} onChange={(e) => set('end_date', e.target.value)} />
                  </Field>
                </div>
                <div className={styles.sentence}>{windowSentence(form.start_date || null, form.end_date || null)}</div>
              </>
            )}
            <div className={styles.grid3}>
              <Field
                label="Countdown in the last … hours"
                hint={recurring ? 'Empty = shown for the whole window. 0 = never.' : 'Empty = 48. 0 = never.'}
              >
                <input
                  className="input"
                  type="number"
                  min={0}
                  max={720}
                  value={form.timer_hours}
                  placeholder={recurring ? 'Whole window' : '48'}
                  onChange={(e) => set('timer_hours', e.target.value)}
                />
              </Field>
              <Field label="Countries" hint="e.g. BE, FR, NL. Empty = all.">
                <input className="input" value={form.countries} placeholder="All countries" onChange={(e) => set('countries', e.target.value.toUpperCase())} />
              </Field>
              <Field label="Priority" hint="Two banners on one product? The highest wins.">
                <input className="input" type="number" value={form.priority} onChange={(e) => set('priority', e.target.value)} />
              </Field>
            </div>
          </section>

          <section className="card" style={{ padding: 18 }}>
            <h2 className={styles.h2}>Look</h2>
            <div className={styles.swatches}>
              {STYLES.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  className={`${styles.swatch} ${form.style === s.key ? styles.swatchOn : ''}`}
                  onClick={() => set('style', s.key)}
                >
                  <span className={styles.swatchChip} style={{ background: s.bg, color: s.fg }}>Aa</span>
                  {s.label}
                </button>
              ))}
            </div>
          </section>

          <section className="card" style={{ padding: 18 }}>
            <div className={styles.h2Row}>
              <h2 className={styles.h2}>Text (English)</h2>
              <Button
                variant="ghost"
                onClick={() => setSuggestOpen((o) => !o)}
                disabled={!aiReady}
                title={aiReady ? 'Let Claude write three options from the campaign details' : aiOffNote}
              >
                <Icon name="sparkle" size={13} /> Suggest copy
              </Button>
            </div>
            {suggestOpen && (
              <div className={styles.suggest}>
                <Field
                  label="Anything to mention? (optional)"
                  hint="Claude already knows the campaign, brands, collection and dates from mosy. It only uses facts you give it."
                >
                  <textarea
                    className="input"
                    rows={2}
                    value={brief}
                    onChange={(e) => setBrief(e.target.value)}
                    placeholder="e.g. Free Fish & Fish carafe (worth €38,50) from €250 on Serax, added to the cart automatically"
                  />
                </Field>
                <div className={styles.suggestActions}>
                  <Button variant="primary" onClick={getSuggestions} disabled={suggesting}>
                    {suggesting ? 'Writing… (about 15 seconds)' : options.length ? 'Try again' : 'Get 3 suggestions'}
                  </Button>
                  <Button variant="ghost" onClick={() => { setSuggestOpen(false); setOptions([]); }}>Close</Button>
                </div>
                {options.length > 0 && (
                  <div className={styles.options}>
                    {options.map((o, i) => (
                      <div key={i} className={styles.option}>
                        <div className={styles.optionAngle}>{o.angle || `Option ${i + 1}`}</div>
                        {o.eyebrow && <div className={styles.optionEyebrow}>{o.eyebrow}</div>}
                        <div className={styles.optionHeadline}>{o.headline}</div>
                        {o.subline && <div className={styles.optionSubline}>{o.subline}</div>}
                        {o.small_print && <div className={styles.optionSmall}>{o.small_print}</div>}
                        <Button onClick={() => applyOption(o)}>Use this</Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            <Field label={COPY_LABEL.eyebrow} hint="Shown in capitals, e.g. FREE GIFT WITH PURCHASE.">
              <input className="input" value={form.eyebrow} onChange={(e) => set('eyebrow', e.target.value)} />
            </Field>
            <Field label={COPY_LABEL.headline} required hint="Press Enter for a new line.">
              <textarea className="input" rows={2} value={form.headline} onChange={(e) => set('headline', e.target.value)} />
            </Field>
            <Field label={COPY_LABEL.subline}>
              <textarea className="input" rows={2} value={form.subline} onChange={(e) => set('subline', e.target.value)} />
            </Field>
            <Field label={COPY_LABEL.small_print}>
              <textarea className="input" rows={3} value={form.small_print} onChange={(e) => set('small_print', e.target.value)} />
            </Field>
          </section>

          <section className="card" style={{ padding: 18 }}>
            <div className={styles.h2Row}>
              <h2 className={styles.h2}>Translations</h2>
              <div className={styles.h2Actions}>
                {banner?.shopify_id && (
                  <Button variant="ghost" onClick={loadFromShop} disabled={loadingShop}>
                    {loadingShop ? 'Reading…' : 'Fill empty ones from the shop'}
                  </Button>
                )}
                <Button
                  onClick={() => translate(false)}
                  disabled={!aiReady || translating}
                  title={aiReady ? 'Claude translates the English into every empty field' : aiOffNote}
                >
                  <Icon name="globe" size={13} /> {translating ? 'Translating… (about 20 seconds)' : 'Translate empty fields'}
                </Button>
              </div>
            </div>
            <p className={styles.help}>
              Empty fields show the English text on the shop.
              {aiReady && (
                <>
                  {' '}Changed the English?{' '}
                  <button
                    type="button"
                    className={styles.linkBtn}
                    style={{ marginTop: 0 }}
                    disabled={translating}
                    onClick={() => {
                      if (window.confirm('Translate every language again? This replaces all translations below.')) translate(true);
                    }}
                  >
                    Translate everything again
                  </button>
                </>
              )}
            </p>
            <div className={styles.tabs}>
              {locales.map((loc) => (
                <button
                  key={loc}
                  type="button"
                  className={`${styles.tab} ${locale === loc ? styles.tabOn : ''}`}
                  onClick={() => setLocale(loc)}
                >
                  {LOCALE_NAME[loc] ?? loc}
                  <span className={styles.tabCount}>{translatedCount(loc)}/4</span>
                </button>
              ))}
            </div>
            {locale && COPY_KEYS.map((k) => (
              <Field key={`${locale}-${k}`} label={COPY_LABEL[k]}>
                {k === 'eyebrow' ? (
                  <input
                    className="input"
                    value={form.translations[locale]?.[k] ?? ''}
                    placeholder={form[k] || '—'}
                    onChange={(e) => setTranslation(locale, k, e.target.value)}
                  />
                ) : (
                  <textarea
                    className="input"
                    rows={k === 'small_print' ? 3 : 2}
                    value={form.translations[locale]?.[k] ?? ''}
                    placeholder={form[k] || '—'}
                    onChange={(e) => setTranslation(locale, k, e.target.value)}
                  />
                )}
              </Field>
            ))}
          </section>
        </div>

        <div className={styles.previewCol}>
          <div className={styles.previewBar}>
            <select className="input" value={previewLocale} onChange={(e) => setPreviewLocale(e.target.value)} style={{ width: 'auto' }}>
              <option value="en">English</option>
              {locales.map((l) => <option key={l} value={l}>{LOCALE_NAME[l] ?? l}</option>)}
            </select>
            <label className={styles.toggle}>
              <input type="checkbox" checked={showCountdown && !timerOff} disabled={timerOff} onChange={(e) => setShowCountdown(e.target.checked)} />
              Countdown
            </label>
            <label className={styles.toggle}>
              <input type="checkbox" checked={narrow} onChange={(e) => setNarrow(e.target.checked)} />
              Phone
            </label>
          </div>
          {previewCopy && (
            <ShopBannerPreview
              style={form.style}
              eyebrow={previewCopy.eyebrow}
              headline={previewCopy.headline}
              subline={previewCopy.subline}
              smallPrint={previewCopy.small_print}
              showCountdown={showCountdown && !timerOff}
              secondsLeft={recurring && repeatMinutes > 0 ? (repeatMinutes - 17) * 60 + 42 : undefined}
              locale={previewLocale}
              width={narrow ? 360 : 600}
            />
          )}
          <div className={styles.previewNote}>
            <Icon name="eye" size={13} />
            <span>
              {timerOff
                ? 'No countdown for this banner.'
                : recurring && !form.timer_hours.trim()
                  ? 'The countdown shows during the whole window.'
                  : `The countdown appears on the shop in the last ${form.timer_hours.trim() || '48'} hours.`}{' '}
              To check it on the real shop before it starts, open a product of the collection and add{' '}
              <code>?campaign-preview</code> to its address (after publishing).
              {form.collection_handle && (
                <> <a href={`${SHOP_URL}/collections/${form.collection_handle}`} target="_blank" rel="noreferrer">Open the collection ↗</a></>
              )}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShopBannerEditor;
